/**
 * Domain Entity: Sharetribe
 * Represents a marketplace item with business logic
 */
export class Sharetribe {
  private constructor(
    private readonly _id: string,
    private readonly _userId: string,
    private readonly _title: string,
    private readonly _description: string,
    private readonly _category: string,
    private readonly _price: number,
    private readonly _currency: string,
    private readonly _images: string[] = [],
    private readonly _location?: string,
    private readonly _latitude?: number,
    private readonly _longitude?: number,
    private readonly _isActive: boolean = true,
    private readonly _isPublished: boolean = false,
    private readonly _views: number = 0,
    private readonly _likes: number = 0,
    private readonly _createdAt: Date = new Date(),
    private readonly _updatedAt: Date = new Date(),
    private readonly _publishedAt?: Date
  ) {}

  static create(
    id: string,
    userId: string,
    title: string,
    description: string,
    category: string,
    price: number,
    currency: string,
    images: string[] = [],
    location?: string,
    latitude?: number,
    longitude?: number
  ): Sharetribe {
    if (!id || !userId || !title || !description || !category) {
      throw new Error('Sharetribe ID, User ID, title, description, and category are required');
    }

    if (title.trim().length === 0) {
      throw new Error('Title cannot be empty');
    }

    if (description.trim().length === 0) {
      throw new Error('Description cannot be empty');
    }

    if (title.length > 200) {
      throw new Error('Title cannot exceed 200 characters');
    }

    if (description.length > 2000) {
      throw new Error('Description cannot exceed 2000 characters');
    }

    if (price < 0) {
      throw new Error('Price cannot be negative');
    }

    return new Sharetribe(id, userId, title, description, category, price, currency, images, location, latitude, longitude);
  }

  static fromData(data: {
    id: string;
    user_id: string;
    title: string;
    description: string;
    category: string;
    price: number;
    currency: string;
    images?: string[];
    location?: string;
    latitude?: number;
    longitude?: number;
    is_active?: boolean;
    is_published?: boolean;
    views?: number;
    likes?: number;
    created_at?: string;
    updated_at?: string;
    published_at?: string;
  }): Sharetribe {
    return new Sharetribe(
      data.id,
      data.user_id,
      data.title,
      data.description,
      data.category,
      data.price,
      data.currency,
      data.images || [],
      data.location,
      data.latitude,
      data.longitude,
      data.is_active || true,
      data.is_published || false,
      data.views || 0,
      data.likes || 0,
      data.created_at ? new Date(data.created_at) : new Date(),
      data.updated_at ? new Date(data.updated_at) : new Date(),
      data.published_at ? new Date(data.published_at) : undefined
    );
  }

  get id(): string {
    return this._id;
  }

  get userId(): string {
    return this._userId;
  }

  get title(): string {
    return this._title;
  }

  get description(): string {
    return this._description;
  }

  get category(): string {
    return this._category;
  }

  get price(): number {
    return this._price;
  }

  get currency(): string {
    return this._currency;
  }

  get images(): string[] {
    return [...this._images];
  }

  get location(): string | undefined {
    return this._location;
  }

  get latitude(): number | undefined {
    return this._latitude;
  }

  get longitude(): number | undefined {
    return this._longitude;
  }

  get isActive(): boolean {
    return this._isActive;
  }

  get isPublished(): boolean {
    return this._isPublished;
  }

  get views(): number {
    return this._views;
  }

  get likes(): number {
    return this._likes;
  }

  get createdAt(): Date {
    return this._createdAt;
  }

  get updatedAt(): Date {
    return this._updatedAt;
  }

  get publishedAt(): Date | undefined {
    return this._publishedAt;
  }

  get hasLocation(): boolean {
    return this._location !== undefined && this._location.length > 0;
  }

  get hasCoordinates(): boolean {
    return this._latitude !== undefined && this._longitude !== undefined;
  }

  get hasImages(): boolean {
    return this._images.length > 0;
  }

  isOwner(userId: string): boolean {
    return this._userId === userId;
  }

  get isDraft(): boolean {
    return !this._isPublished;
  }

  isRecent(days: number = 7): boolean {
    const now = new Date();
    const diffMs = now.getTime() - this._createdAt.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return diffDays <= days;
  }

  get engagementRate(): number {
    if (this._views === 0) return 0;
    return this._likes / this._views;
  }

  addImage(imageUrl: string): Sharetribe {
    if (!imageUrl || imageUrl.trim().length === 0) {
      throw new Error('Image URL cannot be empty');
    }

    return new Sharetribe(
      this._id,
      this._userId,
      this._title,
      this._description,
      this._category,
      this._price,
      this._currency,
      [...this._images, imageUrl],
      this._location,
      this._latitude,
      this._longitude,
      this._isActive,
      this._isPublished,
      this._views,
      this._likes,
      this._createdAt,
      new Date(),
      this._publishedAt
    );
  }

  removeImage(imageUrl: string): Sharetribe {
    return new Sharetribe(
      this._id,
      this._userId,
      this._title,
      this._description,
      this._category,
      this._price,
      this._currency,
      this._images.filter(img => img !== imageUrl),
      this._location,
      this._latitude,
      this._longitude,
      this._isActive,
      this._isPublished,
      this._views,
      this._likes,
      this._createdAt,
      new Date(),
      this._publishedAt
    );
  }

  updateLocation(location: string, latitude?: number, longitude?: number): Sharetribe {
    return new Sharetribe(
      this._id,
      this._userId,
      this._title,
      this._description,
      this._category,
      this._price,
      this._currency,
      this._images,
      location,
      latitude,
      longitude,
      this._isActive,
      this._isPublished,
      this._views,
      this._likes,
      this._createdAt,
      new Date(),
      this._publishedAt
    );
  }

  publish(): Sharetribe {
    return new Sharetribe(
      this._id,
      this._userId,
      this._title,
      this._description,
      this._category,
      this._price,
      this._currency,
      this._images,
      this._location,
      this._latitude,
      this._longitude,
      this._isActive,
      true,
      this._views,
      this._likes,
      this._createdAt,
      new Date(),
      new Date()
    );
  }

  unpublish(): Sharetribe {
    return new Sharetribe(
      this._id,
      this._userId,
      this._title,
      this._description,
      this._category,
      this._price,
      this._currency,
      this._images,
      this._location,
      this._latitude,
      this._longitude,
      this._isActive,
      false,
      this._views,
      this._likes,
      this._createdAt,
      new Date(),
      undefined
    );
  }

  activate(): Sharetribe {
    return new Sharetribe(
      this._id,
      this._userId,
      this._title,
      this._description,
      this._category,
      this._price,
      this._currency,
      this._images,
      this._location,
      this._latitude,
      this._longitude,
      true,
      this._isPublished,
      this._views,
      this._likes,
      this._createdAt,
      new Date(),
      this._publishedAt
    );
  }

  deactivate(): Sharetribe {
    return new Sharetribe(
      this._id,
      this._userId,
      this._title,
      this._description,
      this._category,
      this._price,
      this._currency,
      this._images,
      this._location,
      this._latitude,
      this._longitude,
      false,
      this._isPublished,
      this._views,
      this._likes,
      this._createdAt,
      new Date(),
      this._publishedAt
    );
  }

  view(): Sharetribe {
    return new Sharetribe(
      this._id,
      this._userId,
      this._title,
      this._description,
      this._category,
      this._price,
      this._currency,
      this._images,
      this._location,
      this._latitude,
      this._longitude,
      this._isActive,
      this._isPublished,
      this._views + 1,
      this._likes,
      this._createdAt,
      new Date(),
      this._publishedAt
    );
  }

  like(): Sharetribe {
    return new Sharetribe(
      this._id,
      this._userId,
      this._title,
      this._description,
      this._category,
      this._price,
      this._currency,
      this._images,
      this._location,
      this._latitude,
      this._longitude,
      this._isActive,
      this._isPublished,
      this._views,
      this._likes + 1,
      this._createdAt,
      new Date(),
      this._publishedAt
    );
  }

  toJSON() {
    return {
      id: this._id,
      user_id: this._userId,
      title: this._title,
      description: this._description,
      category: this._category,
      price: this._price,
      currency: this._currency,
      images: this._images,
      location: this._location,
      latitude: this._latitude,
      longitude: this._longitude,
      is_active: this._isActive,
      is_published: this._isPublished,
      views: this._views,
      likes: this._likes,
      created_at: this._createdAt.toISOString(),
      updated_at: this._updatedAt.toISOString(),
      published_at: this._publishedAt?.toISOString()
    };
  }
}
